import { Sprite } from './Sprite';
import { DisplayObject, toI16Wrap } from './DisplayObject';
import { FrameLabel } from './FrameLabel';
import { dispatchRemovedEvents } from './DisplayObjectContainer';
import { rt } from '../player/runtime';
import { runInnerGotoFrame } from '../player/lifecycle';
import { CompiledTimeline, DepthState, compileTimeline, placeProps } from '../symbols/timeline';
import { filterFromDef } from '../filters/filters';
import { TWIPS, fround, toFixed8 } from '../utils/numeric';
import type { FrameScriptOp, SpriteDef } from '../../assets/manifest';
import type { SymbolContext } from '../symbols/types';

/** SWF version of the code that calls gotoAndStop/gotoAndPlay (the port's main SWF is v14). */
const GAME_CALLER_VERSION = 14;

function argumentError(msg: string): Error {
  const e = new Error(`ArgumentError: ${msg}`);
  e.name = 'ArgumentError';
  return e;
}

/** Strict decimal integer parse (AVM2 `string_to_int(s, 10, strict=true)`); NaN when not an integer. */
function strictInt(s: string): number {
  const t = s.trim();
  if (!/^[+-]?\d+$/.test(t)) return NaN;
  return parseInt(t, 10);
}

/**
 * flash.display.MovieClip with AVM2 timeline semantics (see src/flash/player/lifecycle.ts for the
 * per-tick order and docs/analysis for details):
 *
 * - Timeline children persist by depth while their lifetime continues; named children become
 *   parent properties at construction; a timeline removal sets the property to null (unless the
 *   child was touched by addChild/removeChild/setChildIndex).
 * - Once script changes a child's transform (x, y, scale, rotation, alpha, width/height,
 *   transform.*), the timeline no longer applies any PlaceObject properties to it (Flash rule).
 * - gotoAndStop/gotoAndPlay construct the target frame immediately and run its frame script
 *   (SWF >= 10 "inner goto frame"); backward gotos rewind from frame 1 keeping persistent children.
 * - A clip never advances before its frame-1 script had a chance to run (new clips "fresh").
 */
/** Defaults Ruffle applies to fields a PlaceObject omitted when it is replayed by a rewind. */
const IDENTITY_MATRIX: readonly [number, number, number, number, number, number] = [1, 0, 0, 1, 0, 0];
const IDENTITY_COLOR_TRANSFORM: readonly [number, number, number, number, number, number, number, number] = [1, 1, 1, 1, 0, 0, 0, 0];

export class MovieClip extends Sprite {
  /** @internal */ $tl: CompiledTimeline | null;
  /** @internal */ $frame: number;
  /** @internal */ $playing: boolean;
  /** @internal frame -> addFrameScript function (null = removed) */ $scripts: Map<number, ((this: any) => any) | null> | null;
  /** @internal the current frame's script has not run yet */ $scriptFresh: boolean;
  /** @internal */ $executingScript: boolean;
  /** @internal */ $queuedGoto: { frame: number; play: boolean } | null;
  /** @internal created since the last frame-script phase */ $fresh: boolean;
  /** @internal */ $enabled: boolean;
  /** @internal */ $trackAsMenu: boolean;

  constructor() {
    super();
    this.$tl = null;
    this.$frame = 1;
    this.$playing = true;
    this.$scripts = null;
    this.$scriptFresh = false;
    this.$executingScript = false;
    this.$queuedGoto = null;
    this.$fresh = true;
    this.$enabled = true;
    this.$trackAsMenu = false;
    rt().fresh.push(this);
  }

  /** @internal Attach a timeline (library instantiation) and run its first frame. */
  $initTimeline(sym: SymbolContext, def: SpriteDef): void {
    this.$sym = sym;
    this.$tl = compileTimeline(def);
    this.$frame = 0;
    this.$playing = true;
    this.$advanceTo(1);
  }

  // ------------------------------------------------------------------ public API
  get currentFrame(): number {
    return Math.max(1, this.$frame);
  }
  get totalFrames(): number {
    return this.$tl ? this.$tl.totalFrames : 1;
  }
  get framesLoaded(): number {
    return this.totalFrames;
  }
  get isPlaying(): boolean {
    return this.$playing;
  }
  get currentLabel(): string | null {
    if (!this.$tl) return null;
    let best: string | null = null;
    for (const l of this.$tl.labels) if (l.frame <= this.$frame) best = l.name;
    return best;
  }
  get currentFrameLabel(): string | null {
    if (!this.$tl) return null;
    let res: string | null = null;
    for (const l of this.$tl.labels) if (l.frame === this.$frame) res = l.name;
    return res;
  }
  get currentLabels(): FrameLabel[] {
    return this.$tl ? this.$tl.labels.map((l) => new FrameLabel(l.name, l.frame)) : [];
  }
  get scenes(): any[] {
    return [this.currentScene];
  }
  get currentScene(): any {
    return { name: 'Scene 1', labels: this.currentLabels, numFrames: this.totalFrames };
  }
  get enabled(): boolean {
    return this.$enabled;
  }
  set enabled(v: boolean) {
    this.$enabled = !!v;
  }
  get trackAsMenu(): boolean {
    return this.$trackAsMenu;
  }
  set trackAsMenu(v: boolean) {
    this.$trackAsMenu = !!v;
  }

  play(): void {
    this.$playing = true;
  }

  stop(): void {
    this.$playing = false;
  }

  gotoAndPlay(frame: number | string, scene: string | null = null): void {
    this.$gotoExplicit(frame, scene, true, GAME_CALLER_VERSION);
  }

  gotoAndStop(frame: number | string, scene: string | null = null): void {
    this.$gotoExplicit(frame, scene, false, GAME_CALLER_VERSION);
  }

  nextFrame(): void {
    if (this.$frame < this.totalFrames) this.$gotoExplicit(this.$frame + 1, null, false, GAME_CALLER_VERSION);
    else this.stop();
  }

  prevFrame(): void {
    if (this.$frame > 1) this.$gotoExplicit(this.$frame - 1, null, false, GAME_CALLER_VERSION);
    else this.stop();
  }

  nextScene(): void {
    /* single-scene timelines */
  }
  prevScene(): void {
    /* single-scene timelines */
  }

  /** addFrameScript(frameIndex0, fn, frameIndex0, fn, ...) — replaces/removes a frame's script. */
  addFrameScript(...frameAndFn: any[]): void {
    const map = this.$scripts || (this.$scripts = new Map());
    for (let i = 0; i + 1 < frameAndFn.length; i += 2) {
      const frame = (frameAndFn[i] | 0) + 1;
      const fn = frameAndFn[i + 1];
      map.set(frame, typeof fn === 'function' ? fn : null);
      if (frame === this.$frame && typeof fn === 'function') {
        this.$scriptFresh = true;
        rt().pendingScripts.add(this);
      }
    }
  }

  // ------------------------------------------------------------------ frame scripts
  /** @internal */ $scriptFor(frame: number): ((this: any) => any) | null {
    if (this.$scripts && this.$scripts.has(frame)) return this.$scripts.get(frame) ?? null;
    const ops = this.$tl?.scriptsAt(frame);
    if (!ops) return null;
    return function (this: MovieClip) {
      this.$runScriptOps(ops);
    };
  }

  /** @internal */ $hasScript(frame: number): boolean {
    if (this.$scripts && this.$scripts.has(frame)) return this.$scripts.get(frame) != null;
    return !!this.$tl?.scriptsAt(frame);
  }

  /** @internal Interpreter for manifest frame scripts (decompiled addFrameScript bodies). */
  $runScriptOps(ops: FrameScriptOp[]): void {
    const v = (this.$sym as SymbolContext | null)?.swfVersion ?? 10;
    for (const op of ops) {
      switch (op.op) {
        case 'stop':
          this.stop();
          break;
        case 'play':
          this.play();
          break;
        case 'gotoAndPlay':
          this.$gotoExplicit(op.frame, null, true, v);
          break;
        case 'gotoAndStop':
          this.$gotoExplicit(op.frame, null, false, v);
          break;
        case 'nextFrame':
          if (this.$frame < this.totalFrames) this.$gotoExplicit(this.$frame + 1, null, false, v);
          else this.stop();
          break;
        case 'prevFrame':
          if (this.$frame > 1) this.$gotoExplicit(this.$frame - 1, null, false, v);
          else this.stop();
          break;
      }
    }
  }

  /** @internal Called by the frame-scripts pass. */
  $runLocalFrameScript(): void {
    if (this.$executingScript) return;
    if (this.$scriptFresh) {
      this.$scriptFresh = false;
      const fn = this.$scriptFor(this.$frame);
      if (fn) {
        this.$executingScript = true;
        try {
          fn.call(this);
        } catch (err) {
          rt().reportError(err, `frame script (frame ${this.$frame})`);
        } finally {
          this.$executingScript = false;
        }
      }
    }
    const q = this.$queuedGoto;
    if (q) {
      this.$queuedGoto = null;
      this.$gotoNow(q.frame, q.play);
    }
  }

  /** @internal Frame number changed: queue its frame script. */
  $onFrameChanged(): void {
    this.$scriptFresh = true;
    if (this.$hasScript(this.$frame)) rt().pendingScripts.add(this);
    // (what a frame shows is its children's: those placed, moved, replaced or removed report their own change)
    this.$invalidateChildren();
  }

  // ------------------------------------------------------------------ gotos
  /** @internal Resolve a frame number or label (Flash rules; SWF >= 11 callers get #2109 for unknown labels). */
  $resolveFrame(frame: number | string, callerVersion: number): number {
    if (typeof frame === 'number' && Number.isInteger(frame)) return Math.max(1, frame);
    const s = String(frame);
    const n = strictInt(s);
    if (!Number.isNaN(n)) return Math.max(1, n);
    const f = this.$tl?.labelMap.get(s);
    if (f !== undefined) return Math.max(1, f);
    if (callerVersion >= 11) throw argumentError(`Error #2109: Frame label ${s} not found in scene ${s}.`);
    return 1;
  }

  /** @internal */ $gotoExplicit(frame: number | string, _scene: string | null, play: boolean, callerVersion: number): void {
    const f = this.$resolveFrame(frame, callerVersion);
    if (this.$executingScript) {
      // AVM2 queues gotos issued by the clip's own running frame script.
      this.$queuedGoto = { frame: f, play };
      if (this.$hasScript(f)) rt().pendingScripts.add(this);
      return;
    }
    this.$gotoNow(f, play);
  }

  /** @internal */ $gotoNow(frame: number, play: boolean): void {
    this.$playing = play;
    if (frame !== this.$frame) this.$runGoto(frame, false);
    else runInnerGotoFrame(); // no-op gotos still run the nested frame phases in AS3
  }

  /** @internal Run a goto (explicit or the implicit loop back to frame 1). */
  $runGoto(frame: number, implicit: boolean): void {
    const total = this.totalFrames;
    const target = Math.min(Math.max(1, frame), total);
    if (this.$tl) {
      if (target < this.$frame || (target === this.$frame && implicit)) this.$rewindTo(target);
      else if (target > this.$frame) this.$advanceTo(target);
    } else this.$frame = 1;
    if (frame > total) {
      // gotoAndStop(9999) shows the last frame but does not run its actions.
      this.$scriptFresh = false;
      rt().pendingScripts.delete(this);
    }
    if (!implicit) runInnerGotoFrame();
  }

  /** @internal Enter-phase advance (called only for playing, non-fresh clips). */
  $advance(): void {
    const total = this.totalFrames;
    if (this.$frame < total) this.$advanceTo(this.$frame + 1);
    else if (total <= 1) this.$playing = false; // single frame: Flash stops the clip
    else this.$runGoto(1, true);
  }

  $enterFrame(): void {
    super.$enterFrame(); // children first (reverse render order)
    if (this.$fresh || !this.$playing) return;
    this.$advance();
  }

  // ------------------------------------------------------------------ timeline mechanics
  /** @internal Forward to frame `t` (> current) applying the state difference. */
  $advanceTo(t: number): void {
    const tl = this.$tl as CompiledTimeline;
    const f = this.$frame;
    const st = tl.stateAt(t);
    const dm = this.$depthMap;
    if (dm && dm.size) {
      for (const [d, child] of [...dm]) {
        const s = st.get(d);
        if (!s || s.startFrame > f) this.$timelineRemove(child);
      }
    }
    this.$frame = t;
    this.$onFrameChanged();
    const depths = [...st.keys()].sort((a, b) => a - b);
    for (const d of depths) {
      const s = st.get(d) as DepthState;
      const child = this.$depthMap?.get(d);
      if (child) {
        if (s.startFrame <= f) {
          if (s.set.charId !== undefined && s.set.charId > f && child.$charId !== s.charId) {
            child.$replaceCharacter(s.charId);
            child.$charId = s.charId;
          }
          this.$applyState(child, placeProps(s, f));
        }
      } else if (s.startFrame > f) {
        this.$instantiateChild(d, s);
      }
    }
  }

  /** @internal Rewind to frame `t` (<= current): replay from frame 1 keeping persistent children. */
  $rewindTo(t: number): void {
    const tl = this.$tl as CompiledTimeline;
    const st = tl.stateAt(t);
    const dm = this.$depthMap;
    if (dm && dm.size) {
      for (const [d, child] of [...dm]) {
        const s = st.get(d);
        const candidate = child.$placeFrame > t || child.$placedByScript || child.$renderKind === 'morph';
        let survives = !candidate;
        if (candidate && s) survives = this.$survivesRewind(child, s);
        if (!survives) this.$timelineRemove(child);
        else if (s && child.$placeFrame > t) child.$placeFrame = s.startFrame;
      }
    }
    this.$frame = t;
    this.$onFrameChanged();
    const depths = [...st.keys()].sort((a, b) => a - b);
    for (const d of depths) {
      const s = st.get(d) as DepthState;
      const child = this.$depthMap?.get(d);
      if (child) {
        if (child.$charId !== s.charId && (child.$renderKind === 'shape' || child.$renderKind === 'morph')) {
          child.$replaceCharacter(s.charId);
          child.$charId = s.charId;
        }
        this.$applyState(child, placeProps(s, 0));
      } else {
        this.$instantiateChild(d, s);
      }
    }
  }

  /**
   * @internal Ruffle `survives_rewind` for removal candidates. A rewind compares the child against the placement of
   * the target frame; Ruffle's GotoPlaceObject fills the fields a Place left out with their defaults (ratio 0,
   * identity matrix and colour transform), so a missing field means "the default", never "any value". Flash CS
   * writes ratio = keyframe index - 1 as the identity of every keyframe instance and omits it on frame 1, so
   * treating a missing ratio as a wildcard kept the later instance alive: a worm that finished its knock-back,
   * walk or jump-rope clip stayed on that clip (still looping) instead of going back to the frame-1 idle one.
   * clipDepth is not defaulted in Ruffle, so it stays a wildcard.
   */
  $survivesRewind(child: DisplayObject, s: DepthState): boolean {
    const idEq = child.$charId === s.charId;
    const ratioEq = child.$ratio === (s.ratio ?? 0);
    const clipEq = s.clipDepth === undefined || child.$clipDepth === s.clipDepth;
    const kind = child.$renderKind;
    if (kind === 'container') return ratioEq; // MovieClip / Sprite
    if (kind === 'button' || kind === 'bitmap' || (kind === 'text' && !(child as any).$wasStatic)) return ratioEq && idEq && clipEq;
    let mEq = true;
    {
      const m = child.$m;
      const [a, b, c, d, tx, ty] = s.matrix ?? IDENTITY_MATRIX;
      mEq = m.a === fround(a) && m.b === fround(b) && m.c === fround(c) && m.d === fround(d) && m.tx === Math.round(tx * TWIPS) && m.ty === Math.round(ty * TWIPS);
    }
    let ctEq = true;
    {
      const [rm, gm, bm, am, ro, go, bo, ao] = s.colorTransform ?? IDENTITY_COLOR_TRANSFORM;
      ctEq =
        child.$ctRM === toFixed8(rm) &&
        child.$ctGM === toFixed8(gm) &&
        child.$ctBM === toFixed8(bm) &&
        child.$ctAM === toFixed8(am) &&
        child.$ctRO === toI16Wrap(ro) &&
        child.$ctGO === toI16Wrap(go) &&
        child.$ctBO === toI16Wrap(bo) &&
        child.$ctAO === toI16Wrap(ao);
    }
    return ratioEq && idEq && clipEq && mEq && ctEq;
  }

  /** @internal Apply PlaceObject properties (FilterDefs converted to filter objects). */
  $applyState(child: DisplayObject, props: ReturnType<typeof placeProps>): void {
    const p: any = { ...props };
    if (props.filters) p.filters = props.filters.map(filterFromDef);
    child.$applyPlace(p);
  }

  /** @internal Create a timeline child for a depth state (unconstructed until the construct phase). */
  $instantiateChild(depth: number, s: DepthState): DisplayObject | null {
    const sym = this.$sym as SymbolContext | null;
    if (!sym) return null;
    const child: DisplayObject | null = sym.instantiate(s.charId, s.className);
    if (!child) return null;
    child.$byTimeline = true;
    child.$charId = s.charId;
    child.$placeFrame = s.startFrame;
    if (s.name !== undefined) {
      child.$name = s.name;
      child.$explicitName = true;
    }
    if (s.clipDepth !== undefined) child.$clipDepth = s.clipDepth;
    this.$applyState(child, placeProps(s, 0));
    child.$constructed = false;
    rt().unconstructed.add(child);
    const prev = this.$timelineInsert(child, depth);
    if (prev) dispatchRemovedEvents(prev);
    return child;
  }
}
